// Third party
import React from 'react';
import moment from 'moment';
import PerfectScrollbar from 'react-perfect-scrollbar';

// Components
import CloseButton from '../../buttons/CloseButton';
import { Tooltip } from '../../tooltips';
import { LoadingSpinner } from '../../common';
import { ArrowIcon } from './icons/ArrowIcon';
import Modal from '@/views/components/modals/AccessibleModal';
import { Typography } from '@capintel/component-lib/typography';

// Types
import Locale from '@/types/locale';
import Presentation from '@/types/presentation';
import { ALLOWED_HTML_TAGS } from '@/views/components/i18n/FormattedMessageV2';

// Utils
import { MODAL_SIZES } from '@/utils/constants';
import { FormattedMessage, formatMessage } from '@/utils/i18n';
import { getOmniLocale } from '@/utils/locale';

type ExportPresentationModalProps = {
  handleOnCreateOmniProposal: () => void;
  handleOnCreatePDFProposal: () => void;
  presentationsHistory?: Presentation[] | undefined | null;
  disableOmni: boolean;
  canCreateOmniProposal: {
    isDirty?: boolean;
    hasContact?: boolean;
  };
  presentationTemplates: string[];
  locale: Locale;
  show: boolean;
  onClose: () => void;
};

const ExportPresentationModalV1: React.FC<ExportPresentationModalProps> = ({
  handleOnCreatePDFProposal,
  handleOnCreateOmniProposal,
  presentationsHistory,
  disableOmni,
  canCreateOmniProposal,
  presentationTemplates,
  locale,
  show,
  onClose,
}) => {
  const getOmniTooltip = () => {
    if (canCreateOmniProposal.isDirty) {
      return formatMessage('ExportPresentationModal.Online.UnsavedChanges');
    }

    if (!presentationTemplates?.length) {
      return formatMessage('ExportPresentationModal.Online.NoPresentations');
    }

    return undefined;
  };

  const renderResults = () => {
    if (presentationsHistory === undefined) {
      return <LoadingSpinner className="online-presentation-loading" />;
    }

    if (presentationsHistory === null) {
      return (
        <p className="online-presentation-message error">
          <FormattedMessage id="ExportPresentationModal.PresentationHistory.Error" />
        </p>
      );
    }

    if (presentationsHistory?.length === 0) {
      return (
        <p className="online-presentation-message">
          <FormattedMessage id="ExportPresentationModal.PresentationHistory.Empty" />
        </p>
      );
    }

    return (
      <PerfectScrollbar className="presentations-list" style={{ '--visible-count': 2 } as React.CSSProperties}>
        <ul>
          {presentationsHistory?.map(presentation => (
            <li key={presentation.id}>
              <a href={presentation.url.replace('/builder', `/${getOmniLocale(locale)}/builder`)}>
                <h4>{presentation.title}</h4>
                <p className="date">
                  <FormattedMessage
                    id="ExportPresentationModal.PresentationHistory.CreatedAtDate"
                    values={{ date: moment(presentation.createdAt).format(formatMessage('Common.DateFormats.Short')) }}
                  />
                </p>
                <p className="statuses">
                  <span className="pill type-online">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M4 6H22V4H4C2.9 4 2 4.9 2 6V17H0V20H14V17H4V6ZM23 8H17C16.45 8 16 8.45 16 9V19C16 19.55 16.45 20 17 20H23C23.55 20 24 19.55 24 19V9C24 8.45 23.55 8 23 8ZM22 17H18V10H22V17Z"
                        fill="#7D7D7D"
                      />
                    </svg>
                    <FormattedMessage id="ExportPresentationModal.PresentationHistory.Type.Online" />
                  </span>
                  {presentation.readOnly && (
                    <span className="pill">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path
                          d="M12.0001 5.66667H11.3334V4.33333C11.3334 2.49333 9.84008 1 8.00008 1C6.16008 1 4.66675 2.49333 4.66675 4.33333V5.66667H4.00008C3.26675 5.66667 2.66675 6.26667 2.66675 7V13.6667C2.66675 14.4 3.26675 15 4.00008 15H12.0001C12.7334 15 13.3334 14.4 13.3334 13.6667V7C13.3334 6.26667 12.7334 5.66667 12.0001 5.66667ZM6.00008 4.33333C6.00008 3.22667 6.89341 2.33333 8.00008 2.33333C9.10675 2.33333 10.0001 3.22667 10.0001 4.33333V5.66667H6.00008V4.33333ZM12.0001 13.6667H4.00008V7H12.0001V13.6667ZM8.00008 11.6667C8.73341 11.6667 9.33341 11.0667 9.33341 10.3333C9.33341 9.6 8.73341 9 8.00008 9C7.26675 9 6.66675 9.6 6.66675 10.3333C6.66675 11.0667 7.26675 11.6667 8.00008 11.6667Z"
                          fill="#7D7D7D"
                        />
                      </svg>
                    </span>
                  )}
                </p>
              </a>
            </li>
          ))}
        </ul>
      </PerfectScrollbar>
    );
  };

  return (
    <Modal show={show} onHide={onClose} className="export-presentation-modal" dialogClassName="modal-dialog-centered" size={MODAL_SIZES.SM}>
      <Modal.Header>
        {/* Provisional fix. Pending migration to library modal. */}
        <Typography as={ALLOWED_HTML_TAGS.H2} color="primary" variant="headerXs">
          <FormattedMessage id="ExportPresentationModal.Title" />
        </Typography>
        <CloseButton onClick={onClose} />
      </Modal.Header>

      <Modal.Body>
        <div className="export-presentation-button-container">
          <button className="export-presentation-button" onClick={handleOnCreatePDFProposal}>
            <div className="export-presentation-icons">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M14 2H6C4.9 2 4.01 2.9 4.01 4L4 20C4 21.1 4.89 22 5.99 22H18C19.1 22 20 21.1 20 20V8L14 2ZM6 20V4H13V9H18V20H6Z" fill="#7D7D7D" />
              </svg>
              <ArrowIcon size="small" />
            </div>
            <div className="export-presentation-button-label">
              <FormattedMessage id="ExportPresentationModal.PDF.Label" />
            </div>
            <div className="export-presentation-button-description">
              <FormattedMessage id="ExportPresentationModal.PDF.Description" />
            </div>
          </button>

          <Tooltip disabled={!disableOmni} place="left" message={getOmniTooltip()}>
            <button className="export-presentation-button" onClick={handleOnCreateOmniProposal} disabled={disableOmni}>
              <div className="export-presentation-icons">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M4 6H22V4H4C2.9 4 2 4.9 2 6V17H0V20H14V17H4V6ZM23 8H17C16.45 8 16 8.45 16 9V19C16 19.55 16.45 20 17 20H23C23.55 20 24 19.55 24 19V9C24 8.45 23.55 8 23 8ZM22 17H18V10H22V17Z"
                    fill="#7D7D7D"
                  />
                </svg>
                <ArrowIcon size="small" />
              </div>

              <div className="export-presentation-button-label">
                <FormattedMessage id="ExportPresentationModal.Online.Label" />
              </div>
              <div className="export-presentation-button-description">
                <FormattedMessage id="ExportPresentationModal.Online.Description" />
              </div>
            </button>
          </Tooltip>
        </div>
        <hr />
        <div>
          <h3 className="online-presentation-header">
            <FormattedMessage id="ExportPresentationModal.PresentationHistory.Title" />
          </h3>
          {renderResults()}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default ExportPresentationModalV1;
